import { create } from "zustand";

/**
 * Shared in-flight gate for SpeakerLabelEdit's PATCH calls.
 *
 * Multiple `SpeakerLabelEdit` instances bind to the same
 * `(recordingId, speakerId)` pair (e.g., one per transcript segment that
 * shares the same diarized speaker). Each instance used to carry its own
 * per-component `inFlightRef`, which let two saves race: instance A's
 * failure-revert could clobber instance B's optimistic write before B's
 * own response landed.
 *
 * Lifting the gate to a shared zustand store keyed by `(recordingId,
 * speakerId)` ensures only one PATCH per speaker is in flight at any
 * time. The second concurrent save short-circuits via `tryAcquire`,
 * matching the optimistic-update + sync-event arrival semantics.
 */

type Key = `${string}:${number}`;

const keyFor = (recordingId: string, speakerId: number): Key =>
  `${recordingId}:${speakerId}`;

interface SpeakerLabelInFlightState {
  inFlight: ReadonlySet<Key>;
  /** Returns true when this caller acquired the gate (i.e., no other PATCH
   *  for the same key was running). */
  tryAcquire: (recordingId: string, speakerId: number) => boolean;
  release: (recordingId: string, speakerId: number) => void;
  /** Test-only — clears the in-flight set between specs. */
  resetForTests: () => void;
}

export const useSpeakerLabelInFlight = create<SpeakerLabelInFlightState>(
  (set, get) => ({
    inFlight: new Set<Key>(),
    tryAcquire: (recordingId, speakerId) => {
      const k = keyFor(recordingId, speakerId);
      if (get().inFlight.has(k)) return false;
      set((s) => {
        const next = new Set(s.inFlight);
        next.add(k);
        return { inFlight: next };
      });
      return true;
    },
    release: (recordingId, speakerId) => {
      const k = keyFor(recordingId, speakerId);
      set((s) => {
        if (!s.inFlight.has(k)) return s;
        const next = new Set(s.inFlight);
        next.delete(k);
        return { inFlight: next };
      });
    },
    resetForTests: () => set({ inFlight: new Set<Key>() }),
  }),
);
